import { Card, CardContent, CardHeader } from "@/components/layouts/Card";
import PageLoader from "@/components/loader/PageLoader";
import OptionSelectionModal from "@/components/modals/OptionSelectionModal";
import ReorderableList from "@/components/modals/ReorderableList";
import { useUser } from "@/contexts/UserContext";
import { Option, OptionList } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const AddOptionList = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();

  const [name, setName] = useState("");
  const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [optionLists, setOptionLists] = useState<OptionList[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [allOptions, setAllOptions] = useState<Option[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [optionType, setOptionType] = useState("multiple_choice");
  const [maxSelections, setMaxSelections] = useState<number | null>(null);

  useEffect(() => {
    const apiUrl =
      import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION ?? "";
    const fetchCategories = async () => {
      try {
        if (!loading && axiosInstance && user?.selectedBranch) {
          const response = await axiosInstance.get(
            `${apiUrl}?branch=${user.selectedBranch.id}`,
          );
          setOptionLists(response.data.results);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [axiosInstance, loading, user?.selectedBranch]);

  useEffect(() => {
    const fetchAllOptions = async () => {
      try {
        if (!loading && axiosInstance && user?.selectedBranch) {
          const apiUrl =
            import.meta.env.VITE_APP_REDBIRDPOSBE_OPTION_INFORMATION ?? "";
          let allFetchedOptions: Option[] = [];
          let nextUrl = `${apiUrl}?branch=${user.selectedBranch.id}`;

          while (nextUrl) {
            const response = await axiosInstance.get(nextUrl);
            const { results, next } = response.data;
            allFetchedOptions = [...allFetchedOptions, ...results];
            nextUrl = next;
          }

          setAllOptions(allFetchedOptions);
          setOptions(allFetchedOptions);
        }
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    fetchAllOptions();
  }, [axiosInstance, loading, user?.selectedBranch]);

  useEffect(() => {
    if (searchQuery) {
      const filteredOptions = allOptions.filter((option) =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setOptions(filteredOptions);
    } else {
      setOptions(allOptions);
    }
  }, [searchQuery, allOptions]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading || !axiosInstance) return;
    const selectedBranch = user?.selectedBranch?.id;
    const optionListData = {
      name,
      branch: selectedBranch,
      options: selectedOptions.map((option) => option.id),
      option_type: optionType,
      required: isRequired,
      max_selections: optionType !== "multiple_choice" ? maxSelections : null,
    };
    try {
      const apiUrl =
        import.meta.env.VITE_APP_REDBIRDPOSBE_OPTIONLIST_INFORMATION ?? "";
      const response = await axiosInstance.post(apiUrl, optionListData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.status === 201) {
        toast.success(t("back.management.menu.optionList.successadd"));
        navigate("/menu/option-lists");
      }
    } catch (error) {
      console.error("Error adding option list:", error);
      toast.error(t("back.management.menu.optionList.erroradd"));
    }
  };

  const handleRemoveOption = (option: Option) => {
    setSelectedOptions((prev) =>
      prev.filter((selectedOption) => selectedOption.id !== option.id),
    );
  };

  const handleOptionSave = (selectedOptions: Option[]) => {
    setSelectedOptions(selectedOptions);
    setIsOptionModalOpen(false);
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-xl font-bold">
          {t("back.management.menu.optionList.addNewOptionList")}
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.optionList.generalinfo")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:mb-0 md:w-full">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="name"
                >
                  {t("back.management.menu.optionList.optionListName")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder={t(
                    "back.management.menu.optionList.enterOptionListName",
                  )}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
            </div>
            <div className="my-4 rounded-lg bg-white p-4 shadow">
              <h2 className="text-lg font-semibold">
                {t("back.management.menu.option.optionType")}
              </h2>
              <select
                id="optionType"
                value={optionType}
                onChange={(e) => setOptionType(e.target.value)}
                className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
              >
                <option value="multiple_choice">
                  {t("back.management.menu.option.multipleChoice")}
                </option>
                <option value="checkbox">
                  {t("back.management.menu.option.checkbox")}
                </option>
                <option value="increment_decrement">
                  {t("back.management.menu.option.increment_decrement")}
                </option>
              </select>
            </div>
            {optionType !== "multiple_choice" && (
              <div className="my-4 rounded-lg bg-white p-4 shadow">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="maxSelections"
                >
                  {t("back.management.menu.optionList.maxSelections")}
                </label>
                <input
                  type="number"
                  id="maxSelections"
                  placeholder={t(
                    "back.management.menu.optionList.enterMaxSelections",
                  )}
                  value={maxSelections ?? ""}
                  onChange={(e) => setMaxSelections(parseInt(e.target.value))}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
            )}
            <div className="my-4 rounded-lg bg-white p-4 shadow">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                  className="mr-2 leading-tight"
                />
                {t("back.management.menu.optionList.isRequired")}
              </label>
            </div>
          </div>

          <Card className="mb-4">
            <CardHeader className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t("Linked Options")}</h2>
              <button
                onClick={() => setIsOptionModalOpen(true)}
                className="flex items-center rounded-md bg-gray-500 px-3 py-1 text-white"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t("Add")}
              </button>
            </CardHeader>
            <CardContent>
              <ReorderableList
                items={selectedOptions}
                onReorder={(newOptions) => setSelectedOptions(newOptions)}
                onRemove={handleRemoveOption}
                renderItem={(option) => option.name}
              />
            </CardContent>
          </Card>

          <div className="mt-4 flex justify-end">
            <Link
              to="/menu/option-lists"
              className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.category.cancelbutton")}
            </Link>
            <button
              type="submit"
              className="rounded-md bg-red-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.optionList.confirmbutton")}
            </button>
          </div>
        </form>

        <OptionSelectionModal
          isOpen={isOptionModalOpen}
          onClose={() => setIsOptionModalOpen(false)}
          onSave={handleOptionSave}
          initialSelectedOptions={selectedOptions}
        />
      </div>
    </PageLoader>
  );
};

export default AddOptionList;
